import React, { Fragment, useEffect, useState } from 'react'
import { StandardPage } from '../../components/_EOS/StandardPage'
import { StockMovementSearch } from './StockMovementSearch'
import { Filter } from '../../components/_WMS/FilterComponent'
import { defaultFilters } from './config'
import { useApi } from '../../hooks/useApi'
import { StockMovement } from './Entities/StockMovement'
import { ApiResponse } from '../../typings/ApiResponse'
import {
  GenericOrderBy,
  GenericTable,
  getNewDirection
} from '../../components/_EOS/Table'
import { getStockMovements, exportStockMovementsToExcel } from './api'
import { Status } from '../../hooks/useFetchState'
import { usePagination } from '../../hooks/usePagination'
import { generateTableHeadersStockMovement } from '.'
import { StockMovementButtonBar } from './StockMovementButtonBar'
import { useSnackbar } from '../../hooks/useSnackbar'
import { SnackBar } from '../../components/SnackBar/Snackbar'
import { useProductOrigins } from '../../hooks'

interface StockMovementPageProps {}

export const StockMovementPage: React.FC<StockMovementPageProps> = () => {
  const { isActive, isSuccess, message, openSnackBar } = useSnackbar()
  const [selectedFilters, setSelectedFilters] =
    React.useState<Filter[]>(defaultFilters)
  const { state, callApi, setIdle } = useApi<ApiResponse<StockMovement>>()
  const [orderBy, setOrderBy] = useState<GenericOrderBy<StockMovement>>({
    field: 'movementDate',
    direction: 'desc'
  })
  const [isExporting, setIsExporting] = useState<boolean>(false)
  const pagination = usePagination(20)

  const tableHeadersStockMovement = generateTableHeadersStockMovement()

  const handleSorting = ({
    field,
    direction
  }: GenericOrderBy<StockMovement>) => {
    const auxField = field
    if (auxField === orderBy.field) {
      const newDirection = getNewDirection(orderBy.direction)
      setOrderBy((_prev) => ({ field: auxField, direction: newDirection }))
      return
    }

    setOrderBy({ field, direction })
  }

  // Función para validar si al menos uno de los filtros obligatorios está presente
  const validateRequiredFilters = (filters: Filter[]): boolean => {
    const requiredFilterKeys = ['sku', 'ean13', 'pickingRouteId', 'asnId']
    return filters.some((filter) => requiredFilterKeys.includes(filter.key))
  }

  const handlePaginationChange = (page: number, pageSize: number) => {
    // If the pageSize is different, we should set page 0
    const auxPage = pagination.pageSize !== pageSize ? 0 : page

    requestStockMovements(selectedFilters, auxPage, pageSize)
    pagination.setPagination(auxPage, pageSize)
  }

  const requestStockMovements = (
    filters?: Filter[],
    page?: number,
    pageSize?: number
  ) => {
    // Solo hacer el request si los filtros requeridos están presentes
    if (filters && validateRequiredFilters(filters)) {
      callApi(getStockMovements(filters, page, pageSize, orderBy))
    }
  }

  const handleExportExcel = async () => {
    setIsExporting(true)
    try {
      const response = await exportStockMovementsToExcel(selectedFilters)

      if (response.status === 200 && response.data !== null) {
        const raw = window.atob(response.data.file)
        const uInt8Array = new Uint8Array(raw.length)
        for (let i = 0; i < raw.length; ++i) {
          uInt8Array[i] = raw.charCodeAt(i)
        }
        const link = document.createElement('a')
        const blob = new Blob([uInt8Array], {
          type: response.data.mimeType
        })
        link.style.display = 'none'
        link.href = URL.createObjectURL(blob)
        link.setAttribute('download', response.data.name)
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        openSnackBar('Archivo exportado exitosamente', true)
      }
    } catch (error) {
      openSnackBar('Error al exportar el archivo', false)
    } finally {
      setIsExporting(false)
    }
  }

  useEffect(() => {
    pagination.setPagination(0, pagination.pageSize)
    // Solo hacer el request si no hay filtros (después de limpiar filtros)
    if (selectedFilters.length === 0) {
      requestStockMovements(selectedFilters, 0, pagination.pageSize)
    }

    if (selectedFilters.length === 0) {
      setIdle()
    }
  }, [orderBy, selectedFilters])

  const { getProductOrigins } = useProductOrigins()

  useEffect(() => {
    getProductOrigins()
  }, [])

  return (
    <Fragment>
      <StandardPage pageTitle='Historial de Movimientos de Stock'>
        {/* SEARCH */}
        <StockMovementSearch
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          onSearchClick={() =>
            requestStockMovements(selectedFilters, 0, pagination.pageSize)
          }
          includeAddButton={true}
        />

        {/* BUTTON BAR */}
        <StockMovementButtonBar
          onExportClick={handleExportExcel}
          isExporting={isExporting}
          disabled={!state.data?.items}
        />

        {/* TABLE */}
        <GenericTable<StockMovement>
          headersConfig={tableHeadersStockMovement}
          IDKey={'id'}
          data={state.data?.items}
          pagination={{
            totalItem: state.data?.totalItems || 0,
            page: pagination.page,
            pageSize: pagination.pageSize
          }}
          onPaginationChange={handlePaginationChange}
          isLoading={state.status === Status.Loading}
          onSortChange={handleSorting}
          orderField={orderBy}
        />
      </StandardPage>

      {/* SNACKBAR */}
      <SnackBar
        message={message}
        open={isActive}
        status={isSuccess ? 'success' : 'error'}
      />
    </Fragment>
  )
}
